<script setup>
// FLOW-03a (Task 7.1, depois 7.3; SPEC S2, S4, S5, S7 e Gate 1 Q5) — o detalhe de um gatilho de flow,
// na rota `powerflow/flows/:nome/gatilhos/:gatilhoId` (PLAN §2.3). Recebe o gatilho como o
// `GET /powerflow/gatilhos` o dá (Task 5.4), as últimas chamadas ou batidas (`chamadas`, as 50 mais
// novas, sem corpo e sem header) e, na agenda, as próximas execuções da prévia (P5). Só mostra: quem
// usa liga e pausa (`alternar`), gera a URL nova (`trocarToken`), copia (`copiar`) e abre a execução.
//
// **O webhook**: a URL aparece mascarada, com "Mostrar" e "Copiar" (S2.0: ela é a chave de entrada),
// e "Gerar URL nova" pede confirmação, porque a antiga para de responder na hora (S2). O exemplo de
// `curl` usa o método, o header do segredo (S5) e a mesma máscara da URL.
//
// **A agenda**: a regra, o fuso, as próximas três execuções e as puladas de hoje (Q6).
//
// **As chamadas** mostram também as recusadas (R6), que não viram execução: a coluna da execução diz
// "não virou execução", e o código HTTP é o que quem chamou recebeu. A hora leva os segundos
// (resposta 7 de UX). A chamada da URL de teste (`teste: true`) leva o selo "teste" e nunca é
// execução. Na agenda, a tabela é a última batida (`estado_da_agenda`), que é o que o PowerTools guarda.
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import { humanizar } from '../settings/powerTools/usoDoSegredo';
import {
  DESFECHOS,
  ICONES_DOS_GATILHOS,
  contagensPorGrupo,
  nuncaLigado,
  useGatilhoNaTela,
} from './gatilhosNaTela';
import { useExecucaoNaTela } from './useExecucaoNaTela';

const props = defineProps({
  // `{id, flow_name, gatilho_id, tipo, config, ligado, ligado_em, contagens_do_dia, url | estado_da_agenda}`
  gatilho: { type: Object, required: true },
  // A versão publicada que o disparo roda (R1).
  versao: { type: Number, default: null },
  // `[{at, desfecho, status, execucao_id, teste?}]`, da mais nova para a mais antiga.
  chamadas: { type: Array, default: () => [] },
  // Na agenda: as próximas execuções da prévia, em ISO 8601.
  proximas: { type: Array, default: () => [] },
  // O corpo do exemplo de `curl`, em JSON (o editor monta a partir dos `params`).
  corpoDeExemplo: { type: String, default: '{}' },
  podeOperar: { type: Boolean, default: true },
});

const emit = defineEmits([
  'alternar',
  'trocarToken',
  'copiar',
  'abrirExecucao',
  'voltar',
]);

const { t } = useI18n();
const {
  idioma,
  rotuloDoTipo,
  resumoDaRegra,
  quandoCurto,
  rotuloDoDesfecho,
  contagem,
} = useGatilhoNaTela();
const { haQuanto } = useExecucaoNaTela();

const ehWebhook = computed(() => props.gatilho.tipo === 'webhook');
const config = computed(() => props.gatilho.config || {});
const segredo = computed(() => config.value.segredo || null);
const fuso = computed(() => config.value.fuso || 'America/Sao_Paulo');

const numeros = computed(() => {
  const grupos = contagensPorGrupo(props.gatilho.contagens_do_dia);
  grupos.pulada = Math.max(
    grupos.pulada,
    props.gatilho.estado_da_agenda?.puladas_hoje || 0
  );
  return grupos;
});

// -- A URL: o começo fica à vista, o token só com "Mostrar" (os 4 últimos ajudam a conferir).
const mostrar = ref(false);
const PREFIXO = /^(.*\/webhooks\/powerflow\/)(.+)$/;
const urlNaTela = computed(() => {
  const url = props.gatilho.url || '';
  if (mostrar.value) return url;
  const partes = PREFIXO.exec(url);
  return partes ? `${partes[1]}${'•'.repeat(12)}${partes[2].slice(-4)}` : url;
});

const curl = computed(() => {
  const metodo = config.value.metodo || 'POST';
  const linhas = [`curl -X ${metodo} '${urlNaTela.value}'`];
  const comCorpo = metodo !== 'GET' && metodo !== 'DELETE';
  if (comCorpo) linhas.push(`  -H 'Content-Type: application/json'`);
  if (segredo.value?.tipo === 'hmac_sha256') {
    linhas.push(`  -H '${segredo.value.header}: sha256=<assinatura>'`);
  } else if (segredo.value?.tipo === 'header') {
    linhas.push(`  -H '${segredo.value.header}: <valor do cofre>'`);
  }
  if (comCorpo) linhas.push(`  -d '${props.corpoDeExemplo}'`);
  return linhas.join(' \\\n');
});

// O "Copiado" fica 2 s no botão que copiou.
const copiado = ref('');
function copiar(qual, texto) {
  emit('copiar', texto);
  copiado.value = qual;
  setTimeout(() => {
    if (copiado.value === qual) copiado.value = '';
  }, 2000);
}

const dialogoDeTroca = ref(null);

// -- Datas
const formatoDaData = computed(
  () =>
    new Intl.DateTimeFormat(idioma.value, {
      dateStyle: 'short',
      timeStyle: 'medium',
    })
);
const dataCompleta = instante =>
  instante ? formatoDaData.value.format(new Date(instante)) : '';

const ultima = computed(() => props.chamadas[0] || null);
const ultimaBatida = computed(
  () => props.gatilho.estado_da_agenda?.ultima_prevista || null
);

const GRADE = 'md:grid-cols-[11rem_minmax(0,1fr)_4rem_9rem] md:gap-x-4';
const GRADE_DA_AGENDA = 'md:grid-cols-[11rem_minmax(0,1fr)_9rem] md:gap-x-4';
</script>

<template>
  <section class="flex flex-col gap-4" data-gatilho-detalhe>
    <div>
      <Button
        size="sm"
        variant="link"
        color="slate"
        icon="i-lucide-arrow-left"
        :label="t('POWER_TOOLS.POWERFLOW.TRIGGERS.DETAIL.BACK')"
        @click="emit('voltar')"
      />
    </div>

    <header
      class="flex flex-col gap-4 px-6 py-5 border rounded-pz border-n-weak bg-n-card shadow-pz"
    >
      <div class="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div class="flex items-start min-w-0 gap-3">
          <span
            class="grid size-10 shrink-0 place-items-center rounded-xl bg-n-alpha-black2 text-n-slate-12"
          >
            <span
              class="size-5"
              :class="ICONES_DOS_GATILHOS[gatilho.tipo]"
              aria-hidden="true"
            />
          </span>
          <div class="flex flex-col min-w-0 gap-1">
            <h2 class="mb-0 text-heading-1 text-n-slate-12">
              {{
                t('POWER_TOOLS.POWERFLOW.TRIGGERS.CHIP', {
                  tipo: rotuloDoTipo(gatilho.tipo),
                  resumo: gatilho.gatilho_id,
                })
              }}
            </h2>
            <p
              class="flex flex-wrap items-center mb-0 gap-x-1.5 text-sm text-n-slate-11"
            >
              <span>
                {{
                  t('POWER_TOOLS.POWERFLOW.TRIGGERS.DETAIL.OF_FLOW', {
                    flow: humanizar(gatilho.flow_name),
                  })
                }}
              </span>
              <span class="font-mono text-xs">{{ gatilho.flow_name }}</span>
              <template v-if="versao">
                <span
                  class="rounded-full size-[3px] bg-n-slate-10"
                  aria-hidden="true"
                />
                <span class="text-xs">
                  {{
                    t('POWER_TOOLS.POWERFLOW.TRIGGERS.DETAIL.VERSION', {
                      versao,
                    })
                  }}
                </span>
              </template>
            </p>
          </div>
        </div>

        <label
          class="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg outline outline-1 -outline-offset-1 outline-n-weak text-n-slate-12"
        >
          <Switch
            v-if="podeOperar"
            :model-value="gatilho.ligado"
            data-alternar
            @update:model-value="ligado => emit('alternar', ligado)"
          />
          <span v-if="gatilho.ligado">
            {{ t('POWER_TOOLS.POWERFLOW.TRIGGERS.ON') }}
          </span>
          <span v-else-if="nuncaLigado(gatilho)">
            {{ t('POWER_TOOLS.POWERFLOW.TRIGGERS.FIRST_TIME') }}
          </span>
          <span v-else class="text-n-slate-11">
            {{ t('POWER_TOOLS.POWERFLOW.TRIGGERS.OFF') }}
          </span>
        </label>
      </div>

      <dl
        class="grid grid-cols-2 m-0 gap-x-6 gap-y-3 sm:grid-cols-4"
        data-resumo
      >
        <template v-if="ehWebhook">
          <div class="flex flex-col gap-0.5 min-w-0">
            <dt class="text-xs text-n-slate-11">
              {{ t('POWER_TOOLS.POWERFLOW.TRIGGERS.DETAIL.METHOD') }}
            </dt>
            <dd class="m-0 font-mono text-sm text-n-slate-12">
              {{ config.metodo || 'POST' }}
            </dd>
          </div>
          <div class="flex flex-col gap-0.5 min-w-0">
            <dt class="text-xs text-n-slate-11">
              {{ t('POWER_TOOLS.POWERFLOW.TRIGGERS.DETAIL.RESPONSE') }}
            </dt>
            <dd class="m-0 text-sm text-n-slate-12">
              {{
                config.resposta === 'respond'
                  ? t('POWER_TOOLS.POWERFLOW.TRIGGERS.DETAIL.RESPONSE_WAIT')
                  : t('POWER_TOOLS.POWERFLOW.TRIGGERS.DETAIL.RESPONSE_NOW')
              }}
            </dd>
          </div>
          <div class="flex flex-col gap-0.5 min-w-0">
            <dt class="text-xs text-n-slate-11">
              {{ t('POWER_TOOLS.POWERFLOW.TRIGGERS.DETAIL.SECRET') }}
            </dt>
            <dd class="flex flex-col m-0 text-sm text-n-slate-12">
              <template v-if="segredo">
                <span>
                  {{
                    segredo.tipo === 'hmac_sha256'
                      ? t('POWER_TOOLS.POWERFLOW.TRIGGERS.DETAIL.SECRET_HMAC')
                      : t('POWER_TOOLS.POWERFLOW.TRIGGERS.DETAIL.SECRET_HEADER')
                  }}
                </span>
                <span class="font-mono text-xs break-all text-n-slate-11">
                  {{
                    t('POWER_TOOLS.POWERFLOW.TRIGGERS.DETAIL.SECRET_VAULT', {
                      header: segredo.header,
                      entrada: segredo.cofre,
                    })
                  }}
                </span>
              </template>
              <span v-else class="text-n-slate-11">
                {{ t('POWER_TOOLS.POWERFLOW.TRIGGERS.DETAIL.SECRET_NONE') }}
              </span>
            </dd>
          </div>
        </template>
        <template v-else>
          <div class="flex flex-col gap-0.5 min-w-0 sm:col-span-2">
            <dt class="text-xs text-n-slate-11">
              {{ t('POWER_TOOLS.POWERFLOW.TRIGGERS.DETAIL.SCHEDULE.RULE') }}
            </dt>
            <dd class="flex flex-col m-0 text-sm text-n-slate-12">
              <span>{{ resumoDaRegra(gatilho) }}</span>
              <span
                v-if="config.cron"
                class="font-mono text-xs text-n-slate-11"
              >
                {{ config.cron }}
              </span>
            </dd>
          </div>
          <div class="flex flex-col gap-0.5 min-w-0">
            <dt class="text-xs text-n-slate-11">
              {{ t('POWER_TOOLS.POWERFLOW.TRIGGERS.DETAIL.SCHEDULE.TIMEZONE') }}
            </dt>
            <dd class="m-0 font-mono text-xs leading-5 text-n-slate-12">
              {{ fuso }}
            </dd>
          </div>
        </template>
        <div class="flex flex-col gap-0.5 min-w-0">
          <dt class="text-xs text-n-slate-11">
            {{
              ehWebhook
                ? t('POWER_TOOLS.POWERFLOW.TRIGGERS.DETAIL.TODAY')
                : t('POWER_TOOLS.POWERFLOW.TRIGGERS.DETAIL.RUNS')
            }}
          </dt>
          <dd
            class="flex flex-col m-0 text-sm tabular-nums text-n-slate-12"
            data-contagens
          >
            <span v-if="ehWebhook">{{
              contagem('FIRED', numeros.disparo)
            }}</span>
            <!-- A agenda não tem contagem de disparos do dia (P17): a última batida, e nunca
                 "Nada hoje", que diria que ela não bateu (L9d, O4). -->
            <span v-else data-ultima>
              {{
                ultimaBatida
                  ? t('POWER_TOOLS.POWERFLOW.TRIGGERS.LAST', {
                      quando: quandoCurto(ultimaBatida, fuso),
                    })
                  : t('POWER_TOOLS.POWERFLOW.TRIGGERS.COUNTS.NO_RUN_YET')
              }}
            </span>
            <span
              v-if="numeros.recusa"
              class="inline-flex items-center gap-1 text-xs font-medium text-n-ruby-11"
            >
              <span class="i-lucide-shield-alert size-3.5" aria-hidden="true" />
              {{ contagem('REFUSED', numeros.recusa) }}
            </span>
            <span
              v-if="numeros.pulada || !ehWebhook"
              class="text-xs text-n-slate-11"
              :title="
                t('POWER_TOOLS.POWERFLOW.TRIGGERS.DETAIL.SCHEDULE.SKIPPED_HINT')
              "
            >
              {{
                contagem(
                  ehWebhook ? 'SKIPPED' : 'SKIPPED_TODAY',
                  numeros.pulada
                )
              }}
            </span>
          </dd>
        </div>
      </dl>
    </header>

    <!-- O endereço do webhook, o "Gerar URL nova" e o exemplo de chamada. -->
    <section
      v-if="ehWebhook"
      class="flex flex-col gap-4 px-6 py-5 border rounded-pz border-n-weak bg-n-card shadow-pz"
      data-endereco
    >
      <div class="flex flex-col gap-1">
        <h3 class="mb-0 text-heading-2 text-n-slate-12">
          {{ t('POWER_TOOLS.POWERFLOW.TRIGGERS.DETAIL.ADDRESS.TITLE') }}
        </h3>
        <p class="mb-0 text-sm text-n-slate-11">
          {{ t('POWER_TOOLS.POWERFLOW.TRIGGERS.DETAIL.ADDRESS.HINT') }}
        </p>
      </div>
      <div class="flex flex-wrap items-center gap-2">
        <code
          class="flex-1 min-w-[16rem] px-3 py-2 font-mono text-xs leading-5 break-all rounded-lg bg-n-alpha-1 outline outline-1 -outline-offset-1 outline-n-weak text-n-slate-12"
          data-url
        >
          {{ urlNaTela }}
        </code>
        <Button
          size="sm"
          variant="faded"
          color="slate"
          :icon="mostrar ? 'i-lucide-eye-off' : 'i-lucide-eye'"
          :label="
            mostrar
              ? t('POWER_TOOLS.POWERFLOW.TRIGGERS.DETAIL.ADDRESS.HIDE')
              : t('POWER_TOOLS.POWERFLOW.TRIGGERS.DETAIL.ADDRESS.SHOW')
          "
          data-mostrar
          @click="mostrar = !mostrar"
        />
        <Button
          size="sm"
          variant="faded"
          color="slate"
          :icon="copiado === 'url' ? 'i-lucide-check' : 'i-lucide-copy'"
          :label="
            copiado === 'url'
              ? t('POWER_TOOLS.POWERFLOW.TRIGGERS.DETAIL.ADDRESS.COPIED')
              : t('POWER_TOOLS.POWERFLOW.TRIGGERS.DETAIL.ADDRESS.COPY')
          "
          data-copiar-url
          @click="copiar('url', gatilho.url)"
        />
      </div>
      <div
        v-if="podeOperar"
        class="flex flex-wrap items-center gap-x-3 gap-y-1"
      >
        <Button
          size="sm"
          variant="ghost"
          color="ruby"
          icon="i-lucide-refresh-cw"
          :label="t('POWER_TOOLS.POWERFLOW.TRIGGERS.DETAIL.ADDRESS.ROTATE')"
          data-trocar-token
          @click="dialogoDeTroca?.open()"
        />
        <span class="text-xs text-n-slate-11">
          {{ t('POWER_TOOLS.POWERFLOW.TRIGGERS.DETAIL.ADDRESS.ROTATE_HINT') }}
        </span>
      </div>

      <div class="flex flex-col gap-2 pt-4 border-t border-n-weak">
        <div class="flex flex-wrap items-start justify-between gap-2">
          <div class="flex flex-col gap-0.5">
            <h4 class="mb-0 text-sm font-medium text-n-slate-12">
              {{ t('POWER_TOOLS.POWERFLOW.TRIGGERS.DETAIL.EXAMPLE.TITLE') }}
            </h4>
            <p class="mb-0 text-xs text-n-slate-11">
              {{ t('POWER_TOOLS.POWERFLOW.TRIGGERS.DETAIL.EXAMPLE.HINT') }}
              <template v-if="segredo?.tipo === 'hmac_sha256'">
                {{
                  t('POWER_TOOLS.POWERFLOW.TRIGGERS.DETAIL.EXAMPLE.HMAC_HINT')
                }}
              </template>
            </p>
          </div>
          <Button
            size="sm"
            variant="ghost"
            color="slate"
            :icon="copiado === 'curl' ? 'i-lucide-check' : 'i-lucide-copy'"
            :label="t('POWER_TOOLS.POWERFLOW.TRIGGERS.DETAIL.EXAMPLE.COPY')"
            data-copiar-curl
            @click="copiar('curl', curl)"
          />
        </div>
        <pre
          class="p-3 m-0 overflow-x-auto font-mono text-xs leading-5 rounded-lg bg-n-alpha-1 text-n-slate-12"
          data-curl
          >{{ curl }}</pre>
      </div>
    </section>

    <!-- A agenda: as próximas execuções, no fuso da regra. -->
    <section
      v-else
      class="flex flex-col gap-3 px-6 py-5 border rounded-pz border-n-weak bg-n-card shadow-pz"
      data-quando-roda
    >
      <h3 class="mb-0 text-heading-2 text-n-slate-12">
        {{ t('POWER_TOOLS.POWERFLOW.TRIGGERS.DETAIL.SCHEDULE.NEXT') }}
      </h3>
      <ol
        v-if="gatilho.ligado && proximas.length"
        class="flex flex-wrap gap-2 p-0 m-0 list-none"
      >
        <li
          v-for="(instante, i) in proximas"
          :key="instante"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg tabular-nums outline outline-1 -outline-offset-1 outline-n-weak text-n-slate-12"
        >
          <span
            class="size-3.5"
            :class="
              i === 0
                ? 'i-lucide-alarm-clock text-n-brand'
                : 'i-lucide-clock text-n-slate-10'
            "
            aria-hidden="true"
          />
          {{ quandoCurto(instante, fuso) }}
        </li>
      </ol>
      <p v-else class="mb-0 text-sm text-n-slate-11">
        {{ t('POWER_TOOLS.POWERFLOW.TRIGGERS.DETAIL.SCHEDULE.PAUSED_NEXT') }}
      </p>
    </section>

    <!-- As últimas 50 chamadas ou batidas, com as recusadas. -->
    <section
      class="border rounded-pz border-n-weak bg-n-card shadow-pz"
      data-chamadas
    >
      <div
        class="flex flex-wrap items-baseline justify-between gap-2 px-6 pt-5 pb-3"
      >
        <div class="flex flex-col gap-0.5">
          <h3 class="mb-0 text-heading-2 text-n-slate-12">
            {{
              ehWebhook
                ? t('POWER_TOOLS.POWERFLOW.TRIGGERS.DETAIL.CALLS.TITLE')
                : t(
                    'POWER_TOOLS.POWERFLOW.TRIGGERS.DETAIL.CALLS.TITLE_SCHEDULE'
                  )
            }}
          </h3>
          <p class="mb-0 text-xs text-n-slate-11">
            {{
              ehWebhook
                ? t('POWER_TOOLS.POWERFLOW.TRIGGERS.DETAIL.CALLS.SUBTITLE')
                : t(
                    'POWER_TOOLS.POWERFLOW.TRIGGERS.DETAIL.CALLS.SUBTITLE_SCHEDULE'
                  )
            }}
          </p>
        </div>
        <span v-if="ultima" class="text-xs text-n-slate-11">
          {{
            ehWebhook
              ? t('POWER_TOOLS.POWERFLOW.TRIGGERS.DETAIL.LAST_CALL', {
                  quando: haQuanto(ultima.at),
                })
              : t('POWER_TOOLS.POWERFLOW.TRIGGERS.DETAIL.LAST_RUN', {
                  quando: haQuanto(ultima.at),
                })
          }}
        </span>
      </div>

      <p
        v-if="!chamadas.length"
        class="flex items-center gap-2 px-6 pt-2 pb-6 mb-0 text-sm text-n-slate-11"
        data-sem-chamadas
      >
        <span
          class="i-lucide-inbox size-4 shrink-0 text-n-slate-10"
          aria-hidden="true"
        />
        {{
          ehWebhook
            ? t('POWER_TOOLS.POWERFLOW.TRIGGERS.DETAIL.CALLS.EMPTY')
            : t('POWER_TOOLS.POWERFLOW.TRIGGERS.DETAIL.CALLS.EMPTY_SCHEDULE')
        }}
      </p>

      <template v-else>
        <div
          aria-hidden="true"
          class="hidden px-6 py-2.5 text-xs font-medium border-y border-n-weak text-n-slate-11 md:grid"
          :class="ehWebhook ? GRADE : GRADE_DA_AGENDA"
        >
          <span>{{
            t('POWER_TOOLS.POWERFLOW.TRIGGERS.DETAIL.CALLS.WHEN')
          }}</span>
          <span>{{
            t('POWER_TOOLS.POWERFLOW.TRIGGERS.DETAIL.CALLS.OUTCOME')
          }}</span>
          <span v-if="ehWebhook">{{
            t('POWER_TOOLS.POWERFLOW.TRIGGERS.DETAIL.CALLS.HTTP')
          }}</span>
          <span>{{
            t('POWER_TOOLS.POWERFLOW.TRIGGERS.DETAIL.CALLS.EXECUTION')
          }}</span>
        </div>
        <ul class="p-0 m-0 list-none divide-y divide-n-weak">
          <li
            v-for="(chamada, i) in chamadas"
            :key="`${chamada.at}-${i}`"
            class="flex flex-wrap items-center px-6 py-2.5 gap-x-4 gap-y-1 md:grid"
            :class="ehWebhook ? GRADE : GRADE_DA_AGENDA"
            :data-chamada="chamada.desfecho"
          >
            <time
              :datetime="chamada.at"
              class="text-sm tabular-nums text-n-slate-12"
              :title="dataCompleta(chamada.at)"
            >
              {{ quandoCurto(chamada.at, fuso, undefined, { segundos: true }) }}
            </time>
            <span
              class="inline-flex items-center min-w-0 gap-1.5 text-sm text-n-slate-12"
            >
              <span
                class="size-3.5 shrink-0"
                :class="DESFECHOS[chamada.desfecho]?.glifo"
                aria-hidden="true"
              />
              <span class="truncate">{{
                rotuloDoDesfecho(chamada.desfecho)
              }}</span>
              <span
                v-if="chamada.teste"
                class="px-1.5 py-px text-xs rounded-md shrink-0 bg-n-alpha-2 text-n-slate-11"
                data-chamada-de-teste
              >
                {{ t('POWER_TOOLS.POWERFLOW.TRIGGERS.TEST_BADGE') }}
              </span>
            </span>
            <span
              v-if="ehWebhook"
              class="font-mono text-xs tabular-nums text-n-slate-11"
            >
              {{ chamada.status }}
            </span>
            <span class="text-sm">
              <Button
                v-if="chamada.execucao_id"
                size="xs"
                variant="link"
                color="slate"
                trailing-icon
                icon="i-lucide-arrow-up-right"
                :label="
                  t(
                    'POWER_TOOLS.POWERFLOW.TRIGGERS.DETAIL.CALLS.OPEN_EXECUTION'
                  )
                "
                @click="emit('abrirExecucao', chamada.execucao_id)"
              />
              <span v-else class="text-xs text-n-slate-10">
                {{
                  t('POWER_TOOLS.POWERFLOW.TRIGGERS.DETAIL.CALLS.NO_EXECUTION')
                }}
              </span>
            </span>
          </li>
        </ul>
      </template>
    </section>

    <Dialog
      ref="dialogoDeTroca"
      type="alert"
      :title="t('POWER_TOOLS.POWERFLOW.TRIGGERS.DETAIL.ROTATE_DIALOG.TITLE')"
      :description="
        t('POWER_TOOLS.POWERFLOW.TRIGGERS.DETAIL.ROTATE_DIALOG.DESCRIPTION')
      "
      :confirm-button-label="
        t('POWER_TOOLS.POWERFLOW.TRIGGERS.DETAIL.ROTATE_DIALOG.CONFIRM')
      "
      :cancel-button-label="
        t('POWER_TOOLS.POWERFLOW.TRIGGERS.DETAIL.ROTATE_DIALOG.BACK')
      "
      data-dialogo-trocar-token
      @confirm="
        emit('trocarToken');
        dialogoDeTroca?.close();
      "
    />
  </section>
</template>
